/**
 * Interventions — les bons saisis au bureau : liste à gauche, lecture du bon à droite (lot I1.1, ADR-0014).
 *
 * Même mise en page que « Gammes » et Le Parc (retour de Benjamin, 2026-10-08) : chaque colonne défile à la molette
 * indépendamment de l'autre ; l'en-tête du bon — son numéro, ses boutons et les messages du dernier geste — reste
 * collé en haut de la colonne de droite, là où l'on a cliqué ; une recherche en tête de liste ; des filtres rapides
 * (« À valider » d'abord). L'état — recherche, filtre, bon lu, machine filtrée, bon en création — est porté par
 * l'URL : l'écran se copie, se partage et survit à un rechargement.
 *
 * Un bon NAÎT en brouillon à la première sauvegarde (le numéro BI-AAAA-NNNNN lui est alors attribué), se soumet
 * (« à valider »), se rouvre pour être corrigé, ou s'annule avec un motif. Il n'écrit RIEN dans les compteurs ni dans
 * l'entretien tant qu'il n'est pas validé : la validation (« Valider le bon ») écrit ses relevés, son entretien, ses lignes
 * et ses suites à donner, que l'on trie ensuite depuis l'écran « Suites à traiter ».
 *
 * Les boutons dépendent des droits lus dans le jeton, MAIS seule l'API décide : sans le droit, elle refuse, et
 * l'écran affiche son refus. Tant que l'éditeur est ouvert, la liste est verrouillée : le travail non enregistré
 * ne doit pas disparaître en changeant de bon.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  ApiError,
  api,
  type BonDetail,
  type BonResume,
  type FacturationBon,
  type ListeBons,
  type NatureBon,
  type TechnicienBon,
} from '../api/client';
import { useDroitsBons } from '../auth/useDroits';
import { ChoixMachineBon } from '../composants/ChoixMachineBon';
import { DetailBon } from '../composants/DetailBon';
import { EditeurBon, type ResultatEnregistrement } from '../composants/EditeurBon';
import { ErreurFormulaire, Panneau } from '../composants/Formulaire';
import { ValidationBon } from '../composants/ValidationBon';
import {
  CHIPS_STATUT,
  LIBELLE_ETAT,
  actionsDuBon,
  chipParId,
  classeEtat,
  aujourdhuiLocal,
  etatSaisissable,
  interventionAFaire,
  jourFr,
  libelleMachine,
  mentionPraxedo,
  phraseEtat,
  type ActionBon,
} from '../domaine/interventions';
import { raisonNonImprimable } from '../domaine/bon-document';
import { FACTURATIONS, LIBELLE_FACTURATION, classeFacturation } from '../domaine/bon-modeles';
import { phraseValidation } from '../domaine/validation-bon';

/** Combien de bons on charge d'un coup ; « Afficher plus » en demande autant. */
const TAILLE_PAGE = 100;

export function InterventionsPage() {
  const [parametres, setParametres] = useSearchParams();
  const bonId = parametres.get('bon');
  const chip = chipParId(parametres.get('chip'));
  const machineFiltree = parametres.get('machine');
  const machineNouvelle = parametres.get('nouveau');
  /** Une livraison sans machine : le site sur lequel le bon est saisi. */
  const siteNouveau = parametres.get('nouveauSite');
  const facturationFiltree: FacturationBon | null = FACTURATIONS.find((f) => f === parametres.get('fact')) ?? null;
  const droits = useDroitsBons();

  const [recherche, setRecherche] = useState(parametres.get('q') ?? '');
  const [rechercheEffective, setRechercheEffective] = useState(recherche);
  const [liste, setListe] = useState<ListeBons | null>(null);
  const [plus, setPlus] = useState<readonly BonResume[]>([]);
  const [detail, setDetail] = useState<BonDetail | null>(null);
  /** Toutes les natures, archivées comprises : un bon dont la nature est archivée garde son modèle de feuille. */
  const [naturesToutes, setNaturesToutes] = useState<NatureBon[] | null>(null);
  const [techniciens, setTechniciens] = useState<TechnicienBon[]>([]);
  const [erreurListe, setErreurListe] = useState<string | null>(null);
  const [erreurDetail, setErreurDetail] = useState<string | null>(null);

  const [edition, setEdition] = useState(false);
  const [choixMachine, setChoixMachine] = useState(false);
  const [annulation, setAnnulation] = useState(false);
  const [validation, setValidation] = useState(false);
  const [motif, setMotif] = useState('');
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  /** Incrémenté pour relire la liste et le bon affiché après un geste. */
  const [rechargement, setRechargement] = useState(0);
  const derniereSelection = useRef<string | null>(null);
  /** Le libellé de la machine (ou du site) d'un bon en création (lu une fois depuis l'adresse). */
  const [libelleNouvelle, setLibelleNouvelle] = useState<string | null>(null);
  const [libelleFiltre, setLibelleFiltre] = useState<string | null>(null);

  const enCreation = machineNouvelle !== null || siteNouveau !== null;
  const verrouillee = edition || enCreation;
  // Les natures proposées à un NOUVEAU bon : les actives seulement (une nature archivée ne se propose plus).
  const natures: NatureBon[] | null = useMemo(() => naturesToutes?.filter((n) => !n.archived) ?? null, [naturesToutes]);

  /** Écrit l'état de l'écran dans l'URL, sans encombrer l'historique. */
  const majUrl = useCallback(
    (changement: Record<string, string | null>) => {
      const p = new URLSearchParams(parametres);
      for (const [cle, valeur] of Object.entries(changement)) {
        if (valeur === null || valeur === '') p.delete(cle);
        else p.set(cle, valeur);
      }
      setParametres(p, { replace: true });
    },
    [parametres, setParametres],
  );

  // La recherche part après une pause de frappe, et s'écrit dans l'URL.
  useEffect(() => {
    const minuteur = window.setTimeout(() => {
      setRechercheEffective(recherche.trim());
    }, 250);
    return () => window.clearTimeout(minuteur);
  }, [recherche]);

  useEffect(() => {
    if ((parametres.get('q') ?? '') !== rechercheEffective) majUrl({ q: rechercheEffective });
    // `parametres` change à chaque écriture d'URL : seule une nouvelle recherche doit déclencher cette écriture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheEffective]);

  // Natures et techniciens : une fois.
  const chargerNatures = useCallback(async () => {
    try {
      setNaturesToutes(await api.naturesBon(true));
    } catch (e) {
      setNaturesToutes([]);
      setErreurListe(e instanceof ApiError ? e.message : 'Les natures de bon n’ont pas pu être lues.');
    }
  }, []);

  useEffect(() => {
    void chargerNatures();
    api
      .techniciens()
      .then(setTechniciens)
      .catch(() => undefined);
  }, [chargerNatures]);

  // La liste suit le filtre, la recherche et la machine ; elle repart de zéro à chaque changement.
  useEffect(() => {
    let annule = false;
    api
      .bons({
        ...(rechercheEffective ? { q: rechercheEffective } : {}),
        ...(chip.statuts ? { statuses: chip.statuts } : {}),
        ...(machineFiltree ? { assetId: machineFiltree } : {}),
        ...(facturationFiltree ? { billing: facturationFiltree } : {}),
        take: TAILLE_PAGE,
      })
      .then((l) => {
        if (annule) return;
        setListe(l);
        setPlus([]);
        setErreurListe(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurListe(e instanceof ApiError ? e.message : 'Chargement des bons impossible.');
      });
    return () => {
      annule = true;
    };
  }, [rechercheEffective, chip.statuts, machineFiltree, facturationFiltree, rechargement]);

  // La machine filtrée : son libellé, pour le dire sur le filtre.
  useEffect(() => {
    if (!machineFiltree) {
      setLibelleFiltre(null);
      return;
    }
    let annule = false;
    api
      .asset(machineFiltree)
      .then((m) => {
        if (!annule) setLibelleFiltre(m.label ?? [m.model, m.serialNumber].filter(Boolean).join(' ') ?? 'Machine');
      })
      .catch(() => {
        if (!annule) setLibelleFiltre('cette machine');
      });
    return () => {
      annule = true;
    };
  }, [machineFiltree]);

  // La machine d'un bon en création.
  useEffect(() => {
    if (!machineNouvelle) {
      if (!siteNouveau) setLibelleNouvelle(null);
      return;
    }
    let annule = false;
    api
      .asset(machineNouvelle)
      .then((m) => {
        if (!annule) setLibelleNouvelle(m.label ?? [m.model, m.serialNumber ? `n° ${m.serialNumber}` : null].filter(Boolean).join(' ') ?? 'Machine');
      })
      .catch((e: unknown) => {
        if (!annule) setErreurDetail(e instanceof ApiError ? e.message : 'Machine introuvable.');
      });
    return () => {
      annule = true;
    };
  }, [machineNouvelle, siteNouveau]);

  // Le site d'une livraison en création (sans machine).
  useEffect(() => {
    if (!siteNouveau || machineNouvelle) return;
    let annule = false;
    api
      .site(siteNouveau)
      .then((s) => {
        if (!annule) setLibelleNouvelle(s.name);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurDetail(e instanceof ApiError ? e.message : 'Site introuvable.');
      });
    return () => {
      annule = true;
    };
  }, [siteNouveau, machineNouvelle]);

  const tous: readonly BonResume[] = useMemo(() => [...(liste?.items ?? []), ...plus], [liste, plus]);

  // Rien n'est choisi : on lit le premier bon de la liste plutôt qu'une colonne vide (sauf en création).
  useEffect(() => {
    const premier = tous[0];
    if (!enCreation && !bonId && premier) majUrl({ bon: premier.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tous, bonId, enCreation]);

  // Le bon lu : relu à chaque changement de bon et après chaque geste.
  useEffect(() => {
    // On ne vide la colonne de droite que si l'on CHANGE de bon : relire le même bon garde l'écran en place.
    if (derniereSelection.current !== bonId) {
      derniereSelection.current = bonId;
      setDetail(null);
      setEdition(false);
      setAnnulation(false);
    }
    if (!bonId) return;
    let annule = false;
    api
      .bon(bonId)
      .then((b) => {
        if (annule) return;
        setDetail(b);
        setErreurDetail(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreurDetail(e instanceof ApiError ? e.message : 'Chargement du bon impossible.');
      });
    return () => {
      annule = true;
    };
  }, [bonId, rechargement]);

  const rafraichir = () => setRechargement((n) => n + 1);

  // « Saisir le bon » depuis le planning arrive avec `?saisie=1` : l'éditeur s'ouvre d'emblée sur le bon, UNE fois (le paramètre
  // est retiré aussitôt, un rechargement de la page ne rouvre pas l'éditeur). Sans le droit de saisir, ou sur un bon qui ne
  // s'enregistre plus (à valider, validé, annulé), le bon s'affiche simplement.
  const saisieDemandee = parametres.get('saisie') === '1';
  useEffect(() => {
    if (!saisieDemandee || !detail || detail.id !== bonId) return;
    majUrl({ saisie: null });
    if (droits.peutSaisirBons && etatSaisissable(detail.status)) {
      setSucces(null);
      setErreurAction(null);
      setEdition(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saisieDemandee, detail, bonId]);

  const selectionner = (id: string) => {
    setSucces(null);
    setErreurAction(null);
    majUrl({ bon: id, nouveau: null, nouveauSite: null });
  };

  const choisirChip = (id: string) => {
    setSucces(null);
    setErreurAction(null);
    majUrl({ chip: id === 'tous' ? null : id, bon: null });
  };

  const choisirFacturation = (f: FacturationBon | null) => {
    setSucces(null);
    setErreurAction(null);
    majUrl({ fact: f, bon: null });
  };

  const chargerPlus = async () => {
    try {
      const l = await api.bons({
        ...(rechercheEffective ? { q: rechercheEffective } : {}),
        ...(chip.statuts ? { statuses: chip.statuts } : {}),
        ...(machineFiltree ? { assetId: machineFiltree } : {}),
        ...(facturationFiltree ? { billing: facturationFiltree } : {}),
        take: TAILLE_PAGE,
        skip: tous.length,
      });
      setPlus((p) => [...p, ...l.items]);
    } catch (e) {
      setErreurListe(e instanceof ApiError ? e.message : 'Chargement impossible.');
    }
  };

  /**
   * Un geste sur le bon (soumettre, rouvrir, annuler) : efface les messages, l'exécute, annonce le résultat puis
   * relit. L'erreur est posée APRÈS la relecture, qui ne l'efface pas.
   */
  const agir = async (action: () => Promise<unknown>, message: string) => {
    setErreurAction(null);
    setSucces(null);
    setEnCours(true);
    try {
      await action();
      setSucces(message);
      rafraichir();
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Action impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const lancer = (action: ActionBon) => {
    if (!detail) return;
    if (action === 'modifier') {
      setSucces(null);
      setErreurAction(null);
      setEdition(true);
    } else if (action === 'soumettre') {
      void agir(
        () => api.soumettreBon(detail.id, detail.revision),
        `Bon ${detail.number} soumis : il est à valider.`,
      );
    } else if (action === 'valider') {
      setSucces(null);
      setErreurAction(null);
      setValidation(true);
    } else if (action === 'rouvrir') {
      void agir(
        () => api.rouvrirBon(detail.id, detail.revision),
        detail.planification.jours.length > 0
          ? `Bon ${detail.number} rouvert : l’intervention est de nouveau en cours, au planning.`
          : `Bon ${detail.number} rouvert : il est de nouveau en brouillon.`,
      );
    } else {
      setMotif('');
      setAnnulation(true);
    }
  };

  const confirmerAnnulation = () => {
    if (!detail) return;
    setAnnulation(false);
    void agir(
      () => api.annulerBon(detail.id, detail.revision, motif),
      `Bon ${detail.number} annulé. Son numéro est conservé : il n’y a aucun trou dans la numérotation.`,
    );
  };

  /** L'éditeur a enregistré : le bon créé s'ouvre, le message dit ce qui s'est passé, et le refus d'une soumission s'ajoute APRÈS. */
  const apresEnregistrement = (r: ResultatEnregistrement) => {
    setEdition(false);
    setErreurAction(null);
    const base = `Bon ${r.bon.number} ${r.creation ? 'créé' : 'enregistré'}`;
    // Une intervention du planning reste au planning tant que son bon n'est pas soumis : ce n'est pas un « brouillon ».
    const etat = interventionAFaire(r.bon.status) ? 'l’intervention reste au planning' : 'brouillon';
    setSucces(
      r.soumis
        ? `${base} et soumis : il est à valider.`
        : r.refusSoumission
          ? `${base} (${etat}), mais il n’a pas été soumis :`
          : `${base} (${etat}).`,
    );
    setRechargement((n) => n + 1);
    majUrl({ bon: r.bon.id, nouveau: null, nouveauSite: null });
    if (r.refusSoumission) setErreurAction(r.refusSoumission);
  };

  const semer = async () => {
    setErreurAction(null);
    setEnCours(true);
    try {
      const bilan = await api.semerNaturesBon();
      const nouvelles = bilan.creees.length;
      const modeles = bilan.modelesInstalles.length;
      setSucces(
        nouvelles > 0
          ? `${nouvelles} nature${nouvelles > 1 ? 's' : ''} de bon installée${nouvelles > 1 ? 's' : ''}.`
          : modeles > 0
            ? `${modeles} nature${modeles > 1 ? 's' : ''} de départ ${modeles > 1 ? 'ont' : 'a'} pris son modèle de feuille (dépannage, installation, livraison, location).`
            : 'Les natures de départ étaient déjà installées.',
      );
      await chargerNatures();
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Installation impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const aucuneNature = natures !== null && natures.length === 0;
  const natureDuDetail = naturesToutes ?? [];
  const actions = detail ? actionsDuBon(detail, droits) : [];
  // Imprimer n'est pas un geste du bon (il n'écrit rien) : le lien vit hors de `actionsDuBon`, qui rend une liste vide pour un
  // bon validé ou annulé, et ne se propose que si le bon donne un document (à valider ou validé).
  const imprimable = detail ? raisonNonImprimable(detail) === null : false;

  const messages = (
    <>
      {succes && <div className="message succes">{succes}</div>}
      <ErreurFormulaire
        message={erreurAction?.message ?? null}
        details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
      />
    </>
  );

  return (
    <div className="gammes-page">
      <div className="entete-page">
        <div>
          <h1>Interventions</h1>
          <p>Les bons d’intervention saisis au bureau, après coup.</p>
        </div>
        <div className="saisie-ligne">
          {/* Les natures de bon et leurs contrôles : une page à part, qu'on ouvre rarement (pas d'entrée de menu). */}
          <Link className="bouton-lien secondaire-lien" to="/interventions/reglages" title="Les natures de bon et la liste de leurs contrôles">
            Réglages des bons
          </Link>
          {droits.peutSaisirBons && (
            <button disabled={verrouillee || aucuneNature || natures === null} onClick={() => setChoixMachine(true)}>
              Nouveau bon
            </button>
          )}
        </div>
      </div>

      {!droits.peutSaisirBons && (
        <div className="message info">
          Vous consultez les bons en lecture seule : en saisir demande le droit de saisir les interventions.
        </div>
      )}

      {aucuneNature && (
        <div className="message info">
          Aucune nature de bon n’existe encore (Entretien, Dépannage, Installation…).{' '}
          {droits.peutConfigurerNatures ? (
            <>
              <button className="secondaire" disabled={enCours} onClick={() => void semer()}>
                Installer les natures de départ
              </button>{' '}
              Cinq natures, que l’on pourra ensuite modifier.
            </>
          ) : (
            'Demandez à un administrateur ou à un responsable de les installer : « Nouveau bon » en a besoin.'
          )}
        </div>
      )}

      <div className="gammes-mise-en-page">
        <div className="carte gammes-liste">
          <div className="gammes-liste-entete">
            <input
              type="text"
              autoFocus
              aria-label="Rechercher un bon"
              placeholder="Rechercher : n° du bon, n° Praxedo, machine, client, technicien…"
              value={recherche}
              disabled={verrouillee}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <div className="chips-secondaires" role="group" aria-label="Filtrer par état">
              {CHIPS_STATUT.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`chip${c.id === chip.id ? ' actif' : ''}`}
                  aria-pressed={c.id === chip.id}
                  disabled={verrouillee}
                  onClick={() => choisirChip(c.id)}
                >
                  {c.libelle}
                </button>
              ))}
            </div>
            <div className="chips-secondaires" role="group" aria-label="Filtrer par facturation">
              <button
                type="button"
                className={`chip${facturationFiltree === null ? ' actif' : ''}`}
                aria-pressed={facturationFiltree === null}
                disabled={verrouillee}
                onClick={() => choisirFacturation(null)}
              >
                Toutes factures
              </button>
              {FACTURATIONS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`chip${facturationFiltree === f ? ' actif' : ''}`}
                  aria-pressed={facturationFiltree === f}
                  disabled={verrouillee}
                  onClick={() => choisirFacturation(f)}
                >
                  {LIBELLE_FACTURATION[f]}
                </button>
              ))}
            </div>
            {machineFiltree && (
              <p className="aide">
                Machine : <strong>{libelleFiltre ?? '…'}</strong>{' '}
                <button type="button" className="secondaire discret" disabled={verrouillee} onClick={() => majUrl({ machine: null, bon: null })}>
                  Voir tous les bons
                </button>
              </p>
            )}
            {verrouillee && <p className="aide">Un bon est en cours de saisie : enregistrez ou fermez l’éditeur pour changer de bon.</p>}
            <div className="gammes-liste-outils">
              <span className="aide">
                {liste === null ? 'Chargement…' : `${tous.length}${liste.total > tous.length ? ` sur ${liste.total}` : ''} bon${liste.total > 1 ? 's' : ''}`}
              </span>
            </div>
          </div>

          <div className="gammes-liste-corps">
            {erreurListe && <div className="message erreur">{erreurListe}</div>}
            {liste === null ? (
              !erreurListe && <div className="vide">Chargement…</div>
            ) : tous.length === 0 ? (
              <div className="vide">
                {rechercheEffective || chip.statuts || machineFiltree || facturationFiltree
                  ? 'Aucun bon ne correspond à ces filtres.'
                  : 'Aucun bon d’intervention pour l’instant.'}
              </div>
            ) : (
              <ul className="parc-lignes">
                {tous.map((b) => (
                  <li key={b.id}>
                    <button
                      type="button"
                      className={`parc-ligne${b.id === bonId && !enCreation ? ' actif' : ''}`}
                      aria-pressed={b.id === bonId}
                      disabled={verrouillee}
                      onClick={() => selectionner(b.id)}
                    >
                      <div className="parc-ligne-titre">
                        <strong>{b.number}</strong>
                        <span className="pastilles-bon">
                          {b.billing && (
                            <span className={`pastille-facturation ${classeFacturation(b.billing)}`}>{LIBELLE_FACTURATION[b.billing]}</span>
                          )}
                          <span className={`etat ${classeEtat(b.status)}`}>{LIBELLE_ETAT[b.status]}</span>
                        </span>
                      </div>
                      <div className="aide">
                        {b.nature.label} · {jourFr(b.performedOn)}
                      </div>
                      <div className="aide">{libelleMachine(b.asset)}</div>
                      <div className="aide">
                        {[b.customer?.name, b.technicianLabel, mentionPraxedo(b)].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </button>
                  </li>
                ))}
                {liste.total > tous.length && (
                  <li className="parc-charger-plus">
                    <button type="button" className="secondaire" disabled={verrouillee} onClick={() => void chargerPlus()}>
                      Afficher plus
                    </button>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        <div className="gammes-fiche">
          {/* Un bon en création : l'éditeur occupe la colonne de droite. */}
          {enCreation && (
            <>
              <div className="gammes-fiche-entete">
                <div className="gammes-fiche-titre">
                  <h2>{machineNouvelle ? 'Nouveau bon d’intervention' : 'Nouvelle livraison sans machine'}</h2>
                </div>
                {messages}
              </div>
              {libelleNouvelle === null && !erreurDetail && (
                <div className="vide">{machineNouvelle ? 'Chargement de la machine…' : 'Chargement du site…'}</div>
              )}
              {erreurDetail && <div className="message erreur">{erreurDetail}</div>}
              {libelleNouvelle !== null && natures !== null && (
                <EditeurBon
                  key={`nouveau-${machineNouvelle ?? siteNouveau}`}
                  bon={null}
                  assetId={machineNouvelle}
                  siteId={machineNouvelle ? null : siteNouveau}
                  libelleCible={libelleNouvelle}
                  natures={natures}
                  techniciens={techniciens}
                  onEnregistre={apresEnregistrement}
                  onFermer={() => majUrl({ nouveau: null, nouveauSite: null })}
                  onConflit={() => majUrl({ nouveau: null, nouveauSite: null })}
                />
              )}
            </>
          )}

          {!enCreation && (
            <>
              {!detail && (erreurAction || succes) && <div className="gammes-fiche-entete">{messages}</div>}
              {!bonId && !erreurDetail && (
                <div className="carte">
                  <div className="vide">
                    Choisissez un bon dans la liste pour le lire ici
                    {droits.peutSaisirBons ? ', ou créez-en un avec « Nouveau bon »' : ''}. Un bon n’écrit rien dans les compteurs ni dans
                    l’entretien tant qu’il n’est pas validé.
                  </div>
                </div>
              )}
              {bonId && !detail && !erreurDetail && <div className="vide">Chargement du bon…</div>}
              {erreurDetail && <div className="message erreur">{erreurDetail}</div>}

              {detail && (
                <>
                  <div className="gammes-fiche-entete">
                    <div className="gammes-fiche-titre">
                      <h2>
                        {detail.number} <span className="aide">({detail.nature.label})</span>{' '}
                        <span className="pastilles-bon">
                          {detail.billing && (
                            <span className={`pastille-facturation ${classeFacturation(detail.billing)}`}>{LIBELLE_FACTURATION[detail.billing]}</span>
                          )}
                          <span className={`etat ${classeEtat(detail.status)}`}>{LIBELLE_ETAT[detail.status]}</span>
                        </span>
                      </h2>
                      {!edition && (actions.length > 0 || imprimable) && (
                        <div className="saisie-ligne">
                          {actions.map((a) => (
                            <button
                              key={a.action}
                              className={a.action === 'soumettre' || a.action === 'valider' ? '' : `secondaire${a.action === 'annuler' ? ' danger-discret' : ''}`}
                              disabled={enCours || a.raison !== undefined}
                              title={a.raison}
                              onClick={() => lancer(a.action)}
                            >
                              {a.libelle}
                            </button>
                          ))}
                          {imprimable && (
                            <Link
                              className="bouton-lien secondaire-lien"
                              to={`/interventions/imprimer?bon=${detail.id}`}
                              title="Imprimer ce bon sur une page A4, ou l’enregistrer en PDF"
                            >
                              Imprimer
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="aide">{phraseEtat(detail)}</p>
                    {messages}
                  </div>

                  {edition && etatSaisissable(detail.status) ? (
                    <EditeurBon
                      key={`${detail.id}-${detail.revision}`}
                      bon={detail}
                      assetId={detail.asset?.id ?? null}
                      libelleCible={detail.asset ? libelleMachine(detail.asset) : (detail.site?.name ?? 'Sans machine')}
                      natures={natureDuDetail}
                      techniciens={techniciens}
                      onEnregistre={apresEnregistrement}
                      onFermer={() => setEdition(false)}
                      onConflit={() => {
                        setEdition(false);
                        setErreurAction(null);
                        setSucces('Bon relu : il a été modifié par quelqu’un d’autre depuis votre ouverture.');
                        rafraichir();
                      }}
                    />
                  ) : (
                    <DetailBon bon={detail} onChange={rafraichir} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {choixMachine && (
        <ChoixMachineBon
          onFermer={() => setChoixMachine(false)}
          onChoisie={(m) => {
            setChoixMachine(false);
            setSucces(null);
            setErreurAction(null);
            majUrl({ nouveau: m.id, nouveauSite: null, bon: null });
          }}
          onChoisieSite={(s) => {
            setChoixMachine(false);
            setSucces(null);
            setErreurAction(null);
            majUrl({ nouveauSite: s.id, nouveau: null, bon: null });
          }}
        />
      )}

      {validation && detail && (
        <ValidationBon
          bon={detail}
          onFermer={() => setValidation(false)}
          onValide={(valide) => {
            setValidation(false);
            setErreurAction(null);
            setSucces(phraseValidation(valide));
            rafraichir();
          }}
        />
      )}

      {annulation && detail && (
        <Panneau
          titre={`Annuler le bon ${detail.number}`}
          sousTitre="Le bon reste lisible et son numéro est conservé : aucun trou dans la numérotation."
          onFermer={() => setAnnulation(false)}
          actions={
            <>
              <button type="button" className="secondaire" onClick={() => setAnnulation(false)}>
                Ne pas annuler
              </button>
              <button type="button" className="danger" disabled={motif.trim().length < 3} onClick={confirmerAnnulation}>
                Annuler ce bon
              </button>
            </>
          }
        >
          <div className="champ">
            <label htmlFor="motif-annulation">
              Motif d’annulation <span className="requis" title="Obligatoire"> *</span>
            </label>
            <textarea
              id="motif-annulation"
              rows={4}
              maxLength={500}
              autoFocus
              value={motif}
              placeholder="Pourquoi ce bon est-il annulé ? (saisi sur la mauvaise machine, doublon…)"
              onChange={(e) => setMotif(e.target.value)}
            />
            <p className="aide">Trois caractères au moins. Le motif n’entre pas dans le journal d’audit.</p>
          </div>
        </Panneau>
      )}
    </div>
  );
}
